import type { HelpSectionContent } from './types';

// CB:BEGIN contractor-help-content
export const contractorHelpContent: HelpSectionContent[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    description: 'Understand the full contractor lifecycle in CoreBids.',
    image: '/help/contractor/c01_contractor_portal.png',
    imageAlt: 'Contractor onboarding start screen',
    tags: ['Step-by-step', 'What happens next'],
    overview: 'CoreBids helps you find jobs, submit high-quality bids, and execute with milestone transparency.',
    steps: [
      { title: 'Sign in to contractor dashboard', body: 'Use your contractor account to access opportunities.' },
      { title: 'Check readiness status', body: 'Ensure setup badges indicate you are ready to bid.' },
    ],
    nextStep: 'Complete account verification and payout setup first.',
    notifications: [{ title: 'Setup reminders', body: 'Prompts for missing readiness items.', type: 'action' }],
    mistakesToAvoid: ['Skipping setup details before bidding.'],
    faqs: [{ question: 'Can I bid before setup is complete?', answer: 'You may be blocked or limited until readiness is complete.' }],
  },
  {
    id: 'account-setup-verification',
    title: 'Account Setup & Verification',
    description: 'Complete profile, docs, and payout readiness.',
    image: '/help/contractor/c03_dashboard_setup.png',
    imageAlt: 'Verification and setup status view',
    tags: ['Step-by-step', 'Notifications'],
    overview: 'Verification increases homeowner trust and prevents payout delays.',
    steps: [
      { title: 'Upload required documents', body: 'Provide license/insurance and required compliance files.' },
      { title: 'Complete payout setup', body: 'Finish payment account setup so disbursements are not delayed.' },
    ],
    nextStep: 'You can bid with confidence and fewer blockers.',
    notifications: [{ title: 'Verification updates', body: 'Pending/approved/rejected status changes.', type: 'info' }],
    mistakesToAvoid: ['Letting verification docs expire.', 'Ignoring setup warnings.'],
    faqs: [{ question: 'Why does readiness matter?', answer: 'It impacts bid eligibility and payout speed.' }],
  },
  {
    id: 'dashboard-overview',
    title: 'Dashboard Overview',
    description: 'Use dashboard as your operating center.',
    image: '/help/contractor/c03_dashboard_setup.png',
    imageAlt: 'Contractor dashboard with action areas',
    tags: ['What happens next', 'Common issues'],
    overview: 'Your dashboard centralizes opportunities, active jobs, and required actions.',
    steps: [
      { title: 'Review action-required items first', body: 'Blocking tasks are highest priority.' },
      { title: 'Check available and active job panels', body: 'Plan your work cadence from one place.' },
    ],
    nextStep: 'Open available projects and evaluate fit before bidding.',
    notifications: [{ title: 'Action required', body: 'Highlights tasks that can stall progress.', type: 'action' }],
    mistakesToAvoid: ['Ignoring dashboard action panels for days.'],
    faqs: [{ question: 'What should I do first each day?', answer: 'Clear action-required tasks, then review new opportunities.' }],
  },
  {
    id: 'finding-projects',
    title: 'Finding Projects',
    description: 'Discover opportunities in your service area.',
    image: '/help/contractor/06_available_projects.png',
    imageAlt: 'Current bids and open projects list',
    tags: ['Step-by-step'],
    overview: 'Prioritize projects that align with your crew capacity and specialty.',
    steps: [
      { title: 'Open Search for Projects', body: 'View projects open for bidding.' },
      { title: 'Filter and prioritize', body: 'Focus on high-fit opportunities first.' },
    ],
    nextStep: 'Open project details and prepare bid strategy.',
    notifications: [{ title: 'New project alert', body: 'Notifies when suitable new work is available.', type: 'info' }],
    mistakesToAvoid: ['Bidding too many low-fit jobs.', 'Missing early response windows.'],
    faqs: [{ question: 'How fast should I respond?', answer: 'Early, complete bids often perform better.' }],
  },
  {
    id: 'reviewing-jobs',
    title: 'Reviewing Jobs',
    description: 'Evaluate scope and risk before pricing.',
    image: '/help/contractor/07_project_details.png',
    imageAlt: 'Project details for contractor bid prep',
    tags: ['Step-by-step', 'Common issues'],
    overview: 'A strong review prevents scope mismatch and post-award friction.',
    steps: [
      { title: 'Read scope and constraints', body: 'Confirm all known requirements.' },
      { title: 'Validate timeline feasibility', body: 'Only promise dates you can hit.' },
    ],
    nextStep: 'Submit a clear, complete bid.',
    notifications: [{ title: 'Clarification requests', body: 'May require updates before homeowner decision.', type: 'action' }],
    mistakesToAvoid: ['Submitting bids before reading full details.'],
    faqs: [{ question: 'Should I note assumptions?', answer: 'Yes—clear assumptions increase trust and reduce disputes.' }],
  },
  {
    id: 'submitting-a-bid',
    title: 'Submitting a Bid',
    description: 'Enter pricing, timeline, and notes clearly.',
    image: '/help/contractor/c09_quote_full_form.png',
    imageAlt: 'Bid submission form with highlighted fields',
    tags: ['What to click', 'Best practices'],
    overview: 'A high-quality bid is specific, realistic, and easy for homeowners to compare.',
    steps: [
      { title: 'Click submit bid', body: 'Open the bid entry form from project details.' },
      { title: 'Set accurate pricing and timeline', body: 'Avoid aggressive estimates you cannot support.' },
      { title: 'Add concise notes', body: 'Explain scope clearly and professionally.' },
    ],
    nextStep: 'Monitor bid status and respond quickly to updates.',
    notifications: [{ title: 'Bid submitted', body: 'Confirms homeowner can now review your offer.', type: 'success' }],
    mistakesToAvoid: ['Vague notes.', 'Unrealistic start/completion timing.'],
    faqs: [{ question: 'How do I improve conversion?', answer: 'Be precise, transparent, and timely.' }],
  },
  {
    id: 'after-you-submit-a-bid',
    title: 'After You Submit a Bid',
    description: 'Track status and maintain responsiveness.',
    image: '/help/contractor/c11_review_bid.png',
    imageAlt: 'Bid status tracking view',
    tags: ['What happens next', 'Notifications'],
    overview: 'Fast follow-up can be the difference between selected and skipped.',
    steps: [
      { title: 'Check bid status panel', body: 'Know if your bid is pending, selected, or requires action.' },
      { title: 'Respond to homeowner/platform prompts', body: 'Timely responses build confidence.' },
    ],
    nextStep: 'If selected, move immediately into kickoff and execution.',
    notifications: [{ title: 'Bid selected', body: 'Signals transition into project execution tasks.', type: 'success' }],
    mistakesToAvoid: ['Ignoring follow-up questions.', 'Letting statuses go stale.'],
    faqs: [{ question: 'How often should I check status?', answer: 'At least twice daily while bids are active.' }],
  },
  {
    id: 'winning-a-job',
    title: 'Winning a Job',
    description: 'Kick off quickly after homeowner selection.',
    image: '/help/contractor/c12_awarded_active.png',
    imageAlt: 'Bid selected notification and next action',
    tags: ['What happens next', 'Action required'],
    overview: 'Immediate, clear kickoff communication improves homeowner confidence and execution speed.',
    steps: [
      { title: 'Open selected-bid notification', body: 'Review required kickoff actions immediately.' },
      { title: 'Confirm schedule and responsibilities', body: 'Set clear expectations with homeowner.' },
    ],
    nextStep: 'Start milestone workflow and ongoing progress updates.',
    notifications: [{ title: 'Kickoff action required', body: 'Prompts required tasks after bid selection.', type: 'action' }],
    mistakesToAvoid: ['Delaying kickoff responses after selection.'],
    faqs: [{ question: 'What should I send first?', answer: 'A concise kickoff update with timeline and next checkpoint.' }],
  },
  {
    id: 'project-execution',
    title: 'Project Execution',
    description: 'Execute reliably with visible progress.',
    image: '/help/contractor/c13_milestone_evidence.png',
    imageAlt: 'Project execution timeline and status',
    tags: ['Step-by-step', 'Best practices'],
    overview: 'Execution quality is not just work done—it is communication, evidence, and predictability.',
    steps: [
      { title: 'Follow agreed milestones', body: 'Align work sequencing with milestone expectations.' },
      { title: 'Post regular updates', body: 'Keep homeowner informed before they ask.' },
    ],
    nextStep: 'Submit milestone proof for approval and continue to completion.',
    notifications: [{ title: 'Milestone updates', body: 'Tracks submitted, approved, or follow-up states.', type: 'info' }],
    mistakesToAvoid: ['Silent execution with no updates.'],
    faqs: [{ question: 'How detailed should updates be?', answer: 'Short and specific with clear progress evidence.' }],
  },
  {
    id: 'milestones-uploads',
    title: 'Milestones & Uploads',
    description: 'Provide proof that makes approvals easy.',
    image: '/help/contractor/c13_milestone_evidence.png',
    imageAlt: 'Milestone upload and approval preparation',
    tags: ['What to click', 'Notifications'],
    overview: 'High-quality proof reduces approval delays and support escalations.',
    steps: [
      { title: 'Open milestone item', body: 'Use project workflow to select current milestone.' },
      { title: 'Upload clear evidence', body: 'Add photos/docs that map directly to milestone scope.' },
      { title: 'Submit for approval', body: 'Send for homeowner review once evidence is complete.' },
    ],
    nextStep: 'Monitor approval outcome and proceed to next milestone.',
    notifications: [
      { title: 'Milestone ready for review', body: 'Homeowner receives review request.', type: 'action' },
      { title: 'Milestone approved', body: 'Confirms progression and payout workflow.', type: 'success' },
    ],
    mistakesToAvoid: ['Submitting minimal or unclear proof.', 'Waiting too long after milestone completion.'],
    faqs: [{ question: 'What does good proof look like?', answer: 'Clear before/after and close-up detail tied to scope.' }],
  },
  {
    id: 'completing-a-project',
    title: 'Completing a Project',
    description: 'Close out cleanly and professionally.',
    image: '/help/contractor/c13_milestone_evidence.png',
    imageAlt: 'Final completion status and closeout guidance',
    tags: ['Step-by-step', 'What happens next'],
    overview: 'Strong closeout improves reputation and future conversion performance.',
    steps: [
      { title: 'Verify final scope completion', body: 'Ensure all agreed outcomes are delivered.' },
      { title: 'Submit completion status', body: 'Finalize project lifecycle state in platform.' },
    ],
    nextStep: 'Project is marked complete and archived in history.',
    notifications: [{ title: 'Completion confirmed', body: 'Final completion status acknowledgment.', type: 'success' }],
    mistakesToAvoid: ['Closing project with unresolved action items.'],
    faqs: [{ question: 'What if homeowner has a concern?', answer: 'Address quickly and document resolution clearly.' }],
  },
  // CB:BEGIN contractor-messaging-help
  {
    id: 'messaging-homeowners',
    title: 'Messaging Homeowners',
    description: 'Use in-platform chat to communicate professionally after your bid is accepted.',
    image: '/help/contractor/c12_awarded_active.png',
    imageAlt: 'Active project view with messaging panel',
    tags: ['What to click', 'Common issues'],
    overview: 'After a homeowner accepts your bid, a project chat becomes available. Use it for start date confirmation, on-site questions, and progress updates — not for scope or payment negotiations.',
    steps: [
      { title: 'Open your active project', body: 'Go to your awarded jobs section from the contractor dashboard.' },
      { title: 'Find the Messages or chat panel', body: 'A chat panel appears on the project detail page once you are the selected contractor.' },
      { title: 'Send updates proactively', body: 'Notify the homeowner about start times, delays, or scope clarifications before they become issues.' },
    ],
    nextStep: 'Responsive messaging improves reviews and reduces disputes.',
    notifications: [
      { title: 'New message from homeowner', body: 'You receive an email when the homeowner sends a message. Reply promptly to maintain responsiveness score.', type: 'action' },
    ],
    mistakesToAvoid: [
      'Requesting direct phone/email contact in chat — this violates platform terms and can be flagged.',
      'Using messaging to negotiate price or scope changes — use the formal change order process.',
      'Going silent for more than 48 hours when a homeowner has messaged — this triggers support escalation.',
    ],
    faqs: [
      { question: 'When does messaging become available?', answer: 'After the homeowner accepts your bid and the project is active.' },
      { question: 'Can CoreBids read my messages?', answer: 'Yes. All messages are monitored for PII (phone numbers, emails) and policy compliance.' },
      { question: 'What if I need to discuss a scope change?', answer: 'Use the in-platform change order workflow — do not negotiate changes over chat alone.' },
    ],
  },
  // CB:END contractor-messaging-help
  {
    id: 'notifications-explained',
    title: 'Notifications Explained',
    description: 'Know which alerts are informational vs urgent.',
    image: '/help/contractor/c_appendix_email.png',
    imageAlt: 'Contractor notification examples',
    tags: ['Notifications'],
    overview: 'Treat notifications as your operating queue to avoid avoidable delays.',
    steps: [
      { title: 'Prioritize action-required alerts', body: 'These are blockers and should be resolved first.' },
      { title: 'Use deep links from alerts', body: 'Jump directly to the exact task.' },
    ],
    nextStep: 'Consistent responsiveness improves completion speed and trust.',
    notifications: [
      { title: 'New project alert', body: 'Opportunity available to bid.', type: 'info' },
      { title: 'Bid selected', body: 'You won the job; kickoff tasks begin.', type: 'success' },
      { title: 'Milestone approved', body: 'Move forward to next stage.', type: 'success' },
      { title: 'Action required', body: 'Resolve now to prevent delays.', type: 'action' },
    ],
    mistakesToAvoid: ['Treating action-required as optional.'],
    faqs: [{ question: 'What should I check first daily?', answer: 'Action-required and selected bid notifications.' }],
  },
  {
    id: 'best-practices',
    title: 'Best Practices',
    description: 'Habits that improve bid conversion and execution outcomes.',
    image: '/help/contractor/c09_quote_full_form.png',
    imageAlt: 'Bid quality and best-practice hints',
    tags: ['Best practices', 'Improve your bid'],
    overview: 'Winning consistently is a system: quality bids + fast responses + clean milestone proof.',
    steps: [
      { title: 'Improve your bid', body: 'Use specific scope language, realistic timeline, and clear assumptions.' },
      { title: 'What homeowners expect', body: 'Responsiveness, professionalism, and predictable updates.' },
    ],
    nextStep: 'Apply this framework to every new bid.',
    mistakesToAvoid: ['Overpromising timelines.', 'Sparse or generic bid notes.'],
    faqs: [{ question: 'What drives homeowner trust most?', answer: 'Clarity, consistency, and proactive communication.' }],
  },
  {
    id: 'common-mistakes',
    title: 'Common Mistakes',
    description: 'Avoid patterns that reduce close rate or cause delays.',
    image: '/help/contractor/c11_review_bid.png',
    imageAlt: 'Common mistake examples in bid/project flow',
    tags: ['Common issues', 'Avoid delays'],
    overview: 'Small process misses can cause large schedule and trust impacts.',
    steps: [
      { title: 'Avoid vague bids', body: 'Unclear scope lowers acceptance probability.' },
      { title: 'Avoid delayed uploads', body: 'Late milestone proof delays approvals.' },
      { title: 'Avoid stale status handling', body: 'Missed alerts create preventable friction.' },
    ],
    nextStep: 'Audit your current jobs for any of these risks.',
    mistakesToAvoid: ['Submitting without scope specifics.', 'Ignoring action-required notifications.'],
    faqs: [{ question: 'How do I reduce delays?', answer: 'Respond fast and upload complete evidence as soon as work is done.' }],
  },
  {
    id: 'contact-support',
    title: 'Contact Support',
    description: 'Escalate blockers fast with full context.',
    image: '/help/contractor/c_appendix_email.png',
    imageAlt: 'Support contact CTA and guidance',
    tags: ['Support'],
    overview: 'Support can resolve quickly when you provide exact context.',
    steps: [
      { title: 'Include project ID and screenshot', body: 'Share concrete evidence of the issue.' },
      { title: 'Explain expected vs actual behavior', body: 'Helps support triage faster.' },
    ],
    nextStep: 'Support responds with the quickest unblock path.',
    notifications: [{ title: 'Support follow-up', body: 'Reply promptly to keep resolution moving.', type: 'action' }],
    mistakesToAvoid: ['Submitting vague support requests without details.'],
    faqs: [{ question: 'When should I contact support?', answer: 'When blocked from progressing and self-service guidance does not resolve it.' }],
  },
];
// CB:END contractor-help-content

